import {
  Box,
  Dialog,
  Flex,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ReactComponent as DownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ExchangeIcon } from 'assets/icons/exchange-filled.svg';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  SERVICE_FLOW_BORDER,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { fixDecimal } from 'utils';

type ApproveModalType = {
  onClose: () => void;
  isOpen: boolean;
  loading: boolean;
  data: {
    to: string;
    from: string;
    rate: number;
    amount: number;
    destination_amount: number;
  };
  onButton: () => void;
};

const ApproveModal: React.FC<ApproveModalType> = ({
  onClose,
  isOpen,
  loading,
  data,
  onButton,
}: ApproveModalType) => {
  const { t } = useTranslation('component.exchange.modal');

  return (
    <Box display="flex" alignItems="center">
      <Dialog.Root
        size="md"
        open={isOpen}
        placement="center"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            onClose();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>{t('confirm')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody>
                <VStack align="center" justify="flex-start" w="100%">
                  <VStack w="100%" align="center" justify="center" flex="auto">
                    <ExchangeIcon />
                    <Text fontSize="4xl" fontWeight="500" color="body2" py={2}>
                      {`${data.amount} ${data.from}`}
                    </Text>
                    <DownIcon />
                    <Text fontSize="4xl" fontWeight="500" color="body">
                      {`${fixDecimal({
                        value: data.destination_amount,
                        fractionDigits: 5,
                      })} ${data.to}`}
                    </Text>
                  </VStack>
                  <Box
                    w="100%"
                    mt={2}
                    rounded="md"
                    borderWidth="1px"
                    borderColor={SERVICE_FLOW_BORDER}
                    bg="dark"
                    px={{ base: 3, md: 4 }}
                    py={1}
                  >
                    <VStack align="stretch" gap={0} w="100%">
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={4}
                        py={3}
                        borderBottomWidth="1px"
                        borderBottomColor={SERVICE_FLOW_BORDER}
                      >
                        <Text fontSize="sm" color="body2" flexShrink={0}>
                          {t('from')}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="body"
                          textAlign="right"
                          flex="1"
                          minW={0}
                        >
                          {data.from}
                        </Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={4}
                        py={3}
                        borderBottomWidth="1px"
                        borderBottomColor={SERVICE_FLOW_BORDER}
                      >
                        <Text fontSize="sm" color="body2" flexShrink={0}>
                          {t('to')}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="body"
                          textAlign="right"
                          flex="1"
                          minW={0}
                        >
                          {data.to}
                        </Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={4}
                        py={3}
                      >
                        <Text fontSize="sm" color="body2" flexShrink={0}>
                          {t('rate')}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="body"
                          textAlign="right"
                          flex="1"
                          minW={0}
                        >
                          {`1 ${data.from} = ${data.rate} ${data.to}`}
                        </Text>
                      </Flex>
                    </VStack>
                  </Box>
                </VStack>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                  <Button
                    variant="ghost"
                    title={t('cancel')}
                    onClick={onClose}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                  <Button
                    variant="filled"
                    title={t('confirm')}
                    onClick={() => {
                      onButton();
                      onClose();
                    }}
                    isLoading={loading}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                </HStack>
              </ServiceDialogFooter>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default ApproveModal;
